import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardBody, CardHeader, Col, Row,
  Form, FormGroup, Label, Input, Button
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

// UI Components
import PageTitle from "../../layouts/PageTitle";
import SubmitButton from '../../components/ui/SubmitButton';
import ColorPickerPopover from "../../components/ui/ColorPickerPopover";

// Actions
import { getAttributeFunc } from "../../../store/custom-actions/attributes-actions/GetAttributeSlicer";
import { updateAttributeFunc } from "../../../store/custom-actions/attributes-actions/UpdateAttributeSlicer";
import {
  fetchAttributeValues,
  createAttributeValue,
  updateAttributeValue,
  deleteAttributeValue
} from "../../../store/custom-actions/attributes-actions/attributeValuesSlicer";

// Utilities
import { handleDeleteEntity } from "../../../utils/deleteHelper";

const EditAttributePage = () => {
  // Imports & Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { attributeId } = useParams();

  // Redux Selectors
  const { loading: getAttributesLoading, data: attribute } = useSelector(state => state.getAttributeSlicer);
  const { loading: submitAttributeLoading } = useSelector(state => state.updateAttributeSlicer);
  const { data: values, loading: valuesLoading } = useSelector((state) => state.attributeValuesSlicer);

  // Local State
  const [localValues, setLocalValues] = useState([]);
  const [originalValues, setOriginalValues] = useState([]);
  const [attributeName, setAttributeName] = useState("");
  const [newValue, setNewValue] = useState({ value: "", hex: "#000000" });

  const editedIdsRef = useRef(new Set());
  const isColor = attributeName.toLowerCase() === "color";
  const isNewValueValid = newValue.value.trim() !== "" && (!isColor || newValue.hex.trim() !== "");

  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [selectedColorValueId, setSelectedColorValueId] = useState(null);
  const [colorModalValue, setColorModalValue] = useState("#000000");
  
  /**
   * Effects
   */
  useEffect(() => {
    dispatch(getAttributeFunc(attributeId));
    dispatch(fetchAttributeValues({ attributeId }));
  }, [dispatch, attributeId]);

  useEffect(() => {
    if (attribute) setAttributeName(attribute.name);
  }, [attribute]);

  useEffect(() => {
    const updatedLocals = values.map((newItem) => {
      const isEdited = editedIdsRef.current.has(newItem.id);
      const existing = localValues.find((v) => v.id === newItem.id);
      return isEdited && existing ? existing : newItem;
    });
    setLocalValues(updatedLocals);
    setOriginalValues(values);
  }, [values]);

  /**
   * Handlers
   */
  const handleRemoveValue = (id) => {
    handleDeleteEntity({ id, entityName: "Value", deleteFunc: deleteAttributeValue, dispatch, t });
  };

  const handleSubmitAttribute = async (e) => {
    e.preventDefault();
    await dispatch(updateAttributeFunc({ id: attributeId, data: { name: attributeName } })).unwrap();
  };

  const handleLocalValueChange = (valueId, key, newVal) => {
    setLocalValues(prev => prev.map(val => val.id === valueId ? { ...val, [key]: newVal } : val));
    editedIdsRef.current.add(valueId);
  };

  const hasValueChanged = (id) => {
    const original = originalValues.find(v => v.id === id);
    const modified = localValues.find(v => v.id === id);
    if (!original || !modified) return false;

    const trim = (s) => (s || "").trim();
    const [ov, mv] = [trim(original.value), trim(modified.value)];
    const [oh, mh] = [trim(original.hex), trim(modified.hex)];

    return mv !== "" && (ov !== mv || oh !== mh);
  };

  const handleUpdateValue = async (valueId) => {
    const value = localValues.find(v => v.id === valueId);
    if (!value) return;

    const payload = { value: value.value, hex: isColor ? value.hex : null };
    await dispatch(updateAttributeValue({ valueId, payload })).unwrap();
    editedIdsRef.current.delete(valueId);
  };

  const handleAddValue = async () => {
    if (!newValue.value) return;
    const payload = { value: newValue.value, hex: isColor ? newValue.hex : null };
    const resultAction = await dispatch(createAttributeValue({ attributeId, payload })).unwrap();
    setLocalValues(prev => [...prev, resultAction]);
    setNewValue({ value: "", hex: "#000000" });
  };

  const openColorPicker = (e, valueId, currentHex) => {
    const rect = e.target.getBoundingClientRect();
    const pickerHeight = 300;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow > pickerHeight
      ? rect.bottom + window.scrollY + 5
      : rect.top + window.scrollY - pickerHeight - 5;

    setPickerPosition({ top, left: rect.left + window.scrollX });
    setSelectedColorValueId(valueId);
    setColorModalValue(currentHex || "#000000");
    setShowPicker(true);
  };

  return (
    <>
      <PageTitle motherMenu="Attributes" activeMenu="Edit Attribute" motherMenuPath="/attributes"/>

      <Row>
        <Col xs="12">
          <Card>
            <CardHeader><h4 className="card-title">{t("Edit Attribute")}</h4></CardHeader>
            <CardBody>
              <Form onSubmit={handleSubmitAttribute}>
                <FormGroup>
                  <Label>{t("Attribute Name")}</Label>
                  <Input
                    type="text"
                    value={attributeName}
                    onChange={(e) => setAttributeName(e.target.value)}
                    required
                    disabled={isColor}
                  />
                </FormGroup>
                <div className="text-end">
                  <SubmitButton
                    isLoading={submitAttributeLoading}
                  />
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col xs="12">
          <Card>
            <CardHeader><h4 className="card-title">{t("Edit Values")}</h4></CardHeader>
            <CardBody>
              <h5 className="mb-3">{t("Values")}</h5>

              {localValues.map((value, index) => (
                <Row key={`value-${value.id ?? index}`} className="mb-3">
                  <Col md={isColor ? 4 : 9}>
                    <FormGroup>
                      <Label>{t("Value")}</Label>
                      <Input
                        type="text"
                        value={value.value}
                        onChange={(e) => handleLocalValueChange(value.id, "value", e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  {isColor && (
                    <>
                      <Col md={5}>
                        <FormGroup>
                          <Label>{t("Color")}</Label>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 40,
                                height: 30,
                                backgroundColor: value.hex || "#000000",
                                border: "1px solid #ccc",
                                cursor: "pointer"
                              }}
                              onClick={(e) => openColorPicker(e, value.id, value.hex)}
                            />
                            <Input
                              type="text"
                              value={value.hex}
                              onChange={(e) => handleLocalValueChange(value.id, "hex", e.target.value)}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </>
                  )}
                  <Col md="3" className="d-flex align-items-center">
                    <Button color="success" className="btn-sm me-2" onClick={() => handleUpdateValue(value.id)}
                      disabled={!hasValueChanged(value.id) || valuesLoading.update}>
                      <i className="fa fa-edit" />
                    </Button>
                    <Button color="danger" className="btn-sm" onClick={() => handleRemoveValue(value.id)}>
                      <i className="fa fa-trash" />
                    </Button>
                  </Col>
                </Row>
              ))}

              <Row className="mb-3">
                <Col md={isColor ? 4 : 9}>
                  <FormGroup>
                    <Label>{t("New Value")}</Label>
                    <Input
                      type="text"
                      value={newValue.value}
                      onChange={(e) => setNewValue({ ...newValue, value: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                {isColor && (
                  <>
                    <Col md={5}>
                      <FormGroup>
                        <Label>{t("Color")}</Label>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 40,
                              height: 30,
                              backgroundColor: newValue.hex || "#000000",
                              border: "1px solid #ccc",
                              cursor: "pointer"
                            }}
                            onClick={(e) => openColorPicker(e, "new", newValue.hex)}
                          />

                          <Input
                            type="text"
                            value={newValue.hex}
                            onChange={(e) => setNewValue({ ...newValue, hex: e.target.value })}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  </>
                )}
                <Col md="3" className="d-flex align-items-center">
                  <Button type="button" color="success" className="btn-sm" onClick={handleAddValue}
                    disabled={!isNewValueValid || valuesLoading.create}
                  >
                    <i className="fa fa-plus" />
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Floating Sketch Picker */}
      {showPicker && (
        <ColorPickerPopover
          color={colorModalValue}
          onChange={(hex) => setColorModalValue(hex)}
          onApply={() => {
            if (selectedColorValueId === "new") {
              setNewValue(prev => ({ ...prev, hex: colorModalValue }));
            } else {
              handleLocalValueChange(selectedColorValueId, "hex", colorModalValue);
            }
            setShowPicker(false);
          }}
          position={pickerPosition}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
};
export default EditAttributePage;
